<script setup>
import { ref, computed, watch } from 'vue'
import { useCartStore } from '@/stores/CartStore'
import {
  ExclamationCircleIcon,
} from '@heroicons/vue/24/outline'

const props = defineProps(['classDetails', 'housingSelection', 'mealSelection'])
const emit = defineEmits(['selectHousing', 'selectMeals']);

const classDetails = computed(() => props.classDetails)
const housingSelection = computed(() => props.housingSelection)
const mealSelection = computed(() => props.mealSelection)
const hideHousingMeals = classDetails.value.catalogId === 2691 // Workshop called Painting with Fire, Quarter 4, October 13 - 20 2024:  Block housing choice for this workshop.  It runs a double session and we will sell housing by phone.
const date = classDetails.value.startDateStandard

const cartStore = useCartStore()

const parts = date.split('-')
let skuDate = `${parts[1]}-${parts[2]}-${parts[0].slice(2)}`

const price = (sku) => {
  return cartStore.salesItems.find(item => item.item_code === sku)?.price
}

const cottageCost = computed(() => price(`${skuDate}-C`))
const allMealsSku = `${skuDate}-MEALS`
const allMealsCost = computed(() => price(allMealsSku))
const lunchSku = `${skuDate}-LUNCH`
const lunchCost = computed(() => price(lunchSku))

const roomNumbers = [
  // I Dorm
  'S-I1', 'S-I2', 'SP-I4', 'S-I5', 'S-I6', 'SP-I7', 'S-I8',
  // F Dorm
  'S-F9', 'SP-F10', 'S-F11', 'S-F13', 'SP-F14', 'SP-F16', 'S-F19', 'S-F20',
  // E Dorm
  'SP-E23', 'S-E25', 'Q-P-E27', 'S-E28', 'S-E29', 'S-E31', 'SDX-EA', 'SDX-EC',
  // L Dorm
  'S-L33', 'S-L34', 'S-L35', 'SP-L37', 'S-L38', 'SP-L39', 'S-L40', 'SP-L41',
  // Special Housing
  'C'
]
const availability = computed(() => {
  return cartStore.salesItems.reduce((acc, item) => {
    if (item.item_code.includes(skuDate)) {
      acc[item.item_code] = item.quantity
    }
    return acc
  }, {})
})

const totalAvailable = computed(() => {
  let total = 0
  roomNumbers.forEach(room => {
    const key = `${skuDate}-${room}`
    const quantity = availability.value[key]
    if(quantity === null || quantity === undefined) {
      console.error('availability', skuDate, 'room', room, 'key', key, 'quantity', quantity)
    } else {
      total += parseInt(quantity)
    }
  })
  console.log('totalAvailable', total)
  return total
})

const isTaken = (room) => {
  const key = `${skuDate}-${room}`
  return parseInt(availability.value[key]) <= 0
}

const selectedMeals = ref(null)

const mealOptions = computed(() => ({
  [allMealsSku]: `All Meals – $${allMealsCost.value}`,
  [lunchSku]: `Lunches & Welcome Dinner – $${lunchCost.value}`
}))

const mealsDropdown = computed(() => ({
  options: ['No Meals', `All Meals – $${allMealsCost.value}`, `Lunches & Welcome Dinner – $${lunchCost.value}`],
  selectedItem: selectedMeals.value ? mealOptions.value[selectedMeals.value.item_code] : 'No Meals'
}))

const updateMeals = (option) => {
  if (option.includes('No Meals')) {
    selectedMeals.value = null
    emit('selectMeals', selectedMeals.value)
  } else if (option.includes('All Meals')) {
    selectedMeals.value = {
      item_code: allMealsSku, 
      name: mealOptions.value[allMealsSku], 
      cost: allMealsCost.value
    }
    emit('selectMeals', selectedMeals.value)
  } else if (option.includes('Lunch')) {
    selectedMeals.value = {
      item_code: lunchSku, 
      name: mealOptions.value[lunchSku], 
      cost: lunchCost.value
    }
    emit('selectMeals', selectedMeals.value)
  }
} 

const showHousingModal = ref(false)
const activeRoom = ref(null)
const selectedRoom = ref(null)

const housingBadges = ref({
  standard: { color: '#C2D5F5', title: 'Standard' },
  single_plus: { color: '#FBE1C8', title: 'Single Plus' },
  queen_plus: { color: '#F8C495', title: 'Queen Plus' },
  single_deluxe: { color: '#AED1A1', title: 'Single Deluxe' },
  cottage: { color: '#E68F8F', title: 'Cottage' },
})

watch([housingSelection, mealSelection], ([newHousing, newMeal], [oldHousing, oldMeal]) => {
  console.log('newHosuing', newHousing, 'oldHousing', oldHousing)
  console.log('newMeals', newMeal, 'oldMeals', oldMeal)
  if (newHousing === null) {
    activeRoom.value = null
    selectedRoom.value = null
  }
  if (newMeal === null) {
    selectedMeals.value = null
  }
})

const dorms = computed(() => {
  return [
    {
      name: 'I DORM – Female',
      dorm_code: 'I',
      rooms: [
        { num: '1', title: 'Not available' }, // Reserved for instructor
        { 
          num: '2', 
          price: price(`${skuDate}-S-I2`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-I2`,
          is_full: isTaken('S-I2'), 
          is_selected: false 
        },
        { num: '3', title: 'Not available' },
        { 
          num: '4', 
          price: price(`${skuDate}-SP-I4`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-I4`,
          is_full: isTaken('SP-I4'), 
          is_selected: false 
        },
        { title: 'Bathroom' },
        {  },
        { 
          num: '5', 
          price: price(`${skuDate}-S-I5`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-I5`,
          is_full: isTaken('S-I5'), 
          is_selected: false 
        },
        { 
          num: '6', 
          price: price(`${skuDate}-S-I6`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-I6`,
          is_full: isTaken('S-I6'), 
          is_selected: false 
        },
        { 
          num: '7', 
          price: price(`${skuDate}-SP-I7`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-I7`,
          is_full: isTaken('SP-I7'), 
          is_selected: false 
        },
        { 
          num: '8', 
          price: price(`${skuDate}-S-I8`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-I8`,
          is_full: isTaken('S-I8'), 
          is_selected: false 
        },
      ]
    },
    {
      name: 'F DORM – All Genders',
      dorm_code: 'F',
      rooms: [
        { 
          num: '9', 
          price: price(`${skuDate}-S-F9`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-F9`,
          is_full: isTaken('S-F9'), 
          is_selected: false 
        },
        { 
          num: '10', 
          price: price(`${skuDate}-SP-F10`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-F10`,
          is_full: isTaken('SP-F10'), 
          is_selected: false 
        },
        { 
          num: '11', 
          price: price(`${skuDate}-S-F11`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-F11`,
          is_full: isTaken('S-F11'), 
          is_selected: false 
        },
        { num: '12', title: 'Not available' },
        { 
          num: '13', 
          price: price(`${skuDate}-S-F13`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-F13`,
          is_full: isTaken('S-F13'), 
          is_selected: false 
        },
        { 
          num: '14', 
          price: price(`${skuDate}-SP-F14`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-F14`,
          is_full: isTaken('SP-F14'), 
          is_selected: false 
        },
        { num: '15', title: 'Not available' }, // Reserved for instructor
        { 
          num: '16', 
          price: price(`${skuDate}-SP-F16`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-F16`,
          is_full: isTaken('SP-F16'), 
          is_selected: false 
        },
        { num: '17', title: 'Not available' }, // Reserved for instructor
        { num: '18', title: 'Not available' }, // Reserved for instructor
        { 
          num: '19', 
          price: price(`${skuDate}-S-F19`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-F19`,
          is_full: isTaken('S-F19'), 
          is_selected: false 
        },
        { 
          num: '20', 
          price: price(`${skuDate}-S-F20`), 
          title: 'Standard',
          item_code: `${skuDate}-S-F20`,
          is_full: isTaken('S-F20'), 
          is_selected: false 
        },
        { num: '21', title: 'Not available' }, // Reserved for instructor
      ]
    },
    {
      name: 'E DORM — All Genders',
      dorm_code: 'E',
      rooms: [
        { num: '22', title: 'Not available' }, // Reserved for instructor
        { 
          num: '23', 
          price: price(`${skuDate}-SP-E23`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-E23`,
          is_full: isTaken('SP-E23'), 
          is_selected: false 
        },
        { title: 'Bathroom' },
        { 
          num: '25', 
          price: price(`${skuDate}-S-E25`), 
          title: 'Standard',  
          item_code: `${skuDate}-S-E25`,
          is_full: isTaken('S-E25'), 
          is_selected: false 
        },
        { num: '26', title: 'Not available' }, // Reserved for instructor
        { 
          num: '27', 
          price: price(`${skuDate}-Q-P-E27`), 
          title: 'Queen Plus', 
          item_code: `${skuDate}-Q-P-E27`,
          is_full: isTaken('Q-P-E27'), 
          is_selected: false 
        },
        { 
          num: '28', 
          price: price(`${skuDate}-S-E28`), 
          title: 'Please call the office to reserve',
          subtitle: 'Accessible room',
          item_code: `${skuDate}-S-E28`,
          is_full: isTaken('S-E28'),
          disabled: true,
          is_selected: false 
        },
        { 
          num: '29', 
          price: price(`${skuDate}-S-E29`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-E29`,
          is_full: isTaken('S-E29'), 
          is_selected: false 
        },
        { num: '30', title: 'Not available' }, // Reserved for instructor
        { 
          num: '31', 
          price: price(`${skuDate}-S-E31`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-E31`,
          is_full: isTaken('S-E31'), 
          is_selected: false 
        },
        { 
          num: 'A', 
          price: price(`${skuDate}-SDX-EA`), 
          title: 'Single Deluxe', 
          item_code: `${skuDate}-SDX-EA`,
          is_full: isTaken('SDX-EA'), 
          is_selected: false 
        },
        { 
          num: 'C', 
          price: price(`${skuDate}-SDX-EC`), 
          title: 'Single Deluxe', 
          item_code: `${skuDate}-SDX-EC`,
          is_full: isTaken('SDX-EC'), 
          is_selected: false 
        },
      ]
    },
    {
      name: 'L DORM – Female',
      dorm_code: 'L',
      rooms: [
        { num: '32', title: 'Not available' }, // Reserved for instructor
        { 
          num: '33', 
          price: price(`${skuDate}-S-L33`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-L33`,
          is_full: isTaken('S-L33'), 
          is_selected: false 
        },
        { 
          num: '34', 
          price: price(`${skuDate}-S-L34`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-L34`,
          is_full: isTaken('S-L34'), 
          is_selected: false 
        },
        { 
          num: '35', 
          price: price(`${skuDate}-S-L35`), 
          title: 'Standard',
          item_code: `${skuDate}-S-L35`,
          is_full: isTaken('S-L35'), 
          is_selected: false 
        },
        { num: '36', title: 'Not available' }, // Reserved for instructor
        { 
          num: '37', 
          price: price(`${skuDate}-SP-L37`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-L37`,
          is_full: isTaken('SP-L37'), 
          is_selected: false 
        },
        { 
          num: '38', 
          price: price(`${skuDate}-S-L38`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-L38`,
          is_full: isTaken('S-L38'), 
          is_selected: false 
        },
        { 
          num: '39', 
          price: price(`${skuDate}-SP-L39`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-L39`,
          is_full: isTaken('SP-L39'), 
          is_selected: false 
        },
        { 
          num: '40', 
          price: price(`${skuDate}-S-L40`), 
          title: 'Standard', 
          item_code: `${skuDate}-S-L40`,
          is_full: isTaken('S-L40'), 
          is_selected: false 
        },
        { 
          num: '41', 
          price: price(`${skuDate}-SP-L41`), 
          title: 'Single Plus', 
          item_code: `${skuDate}-SP-L41`,
          is_full: isTaken('SP-L41'), 
          is_selected: false 
        },
      ]
    },
  ]
})

const roomBgStyle = (room) => {
  if(room.title === housingBadges.value.standard.title) {
    return `background-color: ${housingBadges.value.standard.color}`
  }
  if(room.title === housingBadges.value.single_plus.title) {
    return `background-color: ${housingBadges.value.single_plus.color}`
  }
  if(room.title === housingBadges.value.queen_plus.title) {
    return `background-color: ${housingBadges.value.queen_plus.color}`
  }
  if(room.title === housingBadges.value.single_deluxe.title) {
    return `background-color: ${housingBadges.value.single_deluxe.color}`
  }
}

const unselectRooms = () => {
  dorms.value.forEach(dorm => {
    dorm.rooms.forEach((room) => room.is_selected = false)
  })
}

const selectRoom = (dorm, roomIdx) => {
  // return false // making rooms selection read-only
  const dormRoom = dorm.rooms[roomIdx]
  if (dormRoom.selectable === false || dormRoom.is_full || dormRoom.title === 'Bathroom' || (dormRoom.num && !dormRoom.price)) {
    return false
  } else {
    unselectRooms()
    dorm.rooms[roomIdx].is_selected = true
    activeRoom.value = dorm.rooms[roomIdx]
  }
}

const selectSpecialRoom = (room) => {
  // return false // making rooms selection read-only
  unselectRooms()
  if (room === 'Cottage') {
    activeRoom.value = {
      num: 'Cottage',
      title: 'Cottage',
      dorm_code: 'C',
      item_code: `${skuDate}-C`,
      price: cottageCost.value,
    }
  }
}

const submitSelectedRoom = () => {
  showHousingModal.value = false
  selectedRoom.value = activeRoom.value
  emit('selectHousing', selectedRoom.value)
  selectedMeals.value = {
    item_code: allMealsSku, 
    name: mealOptions.value[allMealsSku], 
    cost: allMealsCost.value
  }
  emit('selectMeals', selectedMeals.value) 
}

const resetSelectedRoom = () => {
  selectedRoom.value = null
  activeRoom.value = null
  dorms.value.forEach(dorm => {
    dorm.rooms.forEach((room) => room.is_selected = false)
  })
  emit('selectHousing', null)
  emit('selectMeals', null)
}

const openModal = () => {
  showHousingModal.value = true
  setTimeout(() => {
    document.body.style.overflow = 'hidden'
  }, 1);
  console.log(document.body.style)
}
const closeModal = () => {
  showHousingModal.value = false
  document.body.style.overflow = 'visible'
}
</script>

<template>
  <section class="housing-meals-section mt-7 mb-2" v-if="!hideHousingMeals">
    <div class="housing-row">
      <h3 class="min-w-[4.5rem] body-bold">Housing</h3>
      <button
        class="outlined-accent-1 add-btn"
        @click="openModal"
        v-html="'View Housing Options'"
      >
      </button>
      <!-- XXX TODO FIX – show View Housing when displayOnly, otherwise, the below logic -->
      <!-- v-html="selectedRoom.title ? 'Change housing' : 'Add Housing'"> -->
      <p v-if="selectedRoom?.title" class="room-info">
        <span v-if="selectedRoom?.num !== 'Cottage'">Room {{ selectedRoom?.num }}, </span>
        {{ selectedRoom?.title }} - ${{ selectedRoom?.price }}
        <button class="body-bold text-accent-1 uppercase" @click="resetSelectedRoom">Remove room</button>
      </p>
    </div>
    <div class="meals-row flex items-center mt-3">
      <h3 class="min-w-[4.5rem] body-bold">Meals</h3>
      <BaseDropdown 
        v-if="!selectedRoom"
        :dropdown="mealsDropdown" 
        class="w-full sm:w-auto sm:min-w-[18.5rem]" 
        @updateSelected="updateMeals"  
      />
      <div v-if="selectedRoom && selectedMeals">
        {{ selectedMeals.name }}
      </div>
    </div>
  </section>

  <BaseModal v-if="showHousingModal" class="housing-base-modal">
    <div class="add-housing-modal">
      <button class="close-modal" @click="closeModal">
        <Icon icon="heroicons-outline:x" class="w-6 h-6" />
      </button>
      <header class="modal-header">
        <h2 class="page-heading">Add Housing</h2>
      </header>
      <div class="modal-content">
        <div class="rooms-warning small-regular !bg-orange-100" v-if="!totalAvailable">
          <ExclamationCircleIcon class="icon" />
          <p>
            Housing is currently sold out on the website; however additional rooms may be available. 
            Call the office to inquire at (413) 268-3101.
          </p>
        </div>
        <div class="housing-info">
          <h3 class="body-bold mb-4">
            Room Key
          </h3>
          <ul class="flex flex-col md:flex-row md:items-center gap-x-8 gap-y-2 mb-4">
            <li v-for="badge in Object.values(housingBadges)" class="flex items-center gap-x-2">
              <span class="w-[18px] h-[18px] inline-block" :style="{ backgroundColor: badge.color }"></span>
              <span class="badge-title" v-html="badge.title"></span>
            </li>
          </ul>
          <p class="body-semibold mb-8">
            Prices shown are for the entire stay.
            See details and pictures <a href="https://www.snowfarm.org/visit/housing" target="_blank" class="text-accent-1 underline">here</a>.
          </p>
        </div>
        <div class="special-rooms">
          <button
            v-if="false"
            class="outlined-accent-1 btn small-regular md:body-bold lg:large-bold"
            :class="{ '!bg-accent-1 !text-white': activeRoom === 'Farmhouse Double' }"
            :style="{ backgroundColor: housingBadges.cottage.color }"
            @click="selectSpecialRoom('Farmhouse Double')"
          >
            Farmhouse Double
          </button>
          <button
            v-if="false"
            class="outlined-accent-1 btn small-regular md:body-bold lg:large-bold"
            :class="{ '!bg-accent-1 !text-white': activeRoom === 'Farmhouse Single' }"
            :style="{ backgroundColor: housingBadges.cottage.color }"
            @click="selectSpecialRoom('Farmhouse Single')"
          >
            Farmhouse Single
          </button>
          <button
            v-if="!isTaken('C')"
            class="outlined-accent-1 btn small-regular md:body-bold lg:large-bold mb-4"
            :class="{ '!bg-accent-1 !text-white': !!activeRoom && activeRoom.num === 'Cottage' }"
            :style="{ backgroundColor: housingBadges.cottage.color }"
            @click="selectSpecialRoom('Cottage')"
          >
            Cottage – &dollar;{{ cottageCost }}
          </button>
          <div
            v-if="isTaken('C')"
            class="outlined-accent-1 btn small-regular md:body-bold lg:large-bold mb-4 !bg-gray-300"
            :style="{ backgroundColor: housingBadges.cottage.color }"
          >
            Cottage Unavailable
          </div>
        </div>
        <div class="mb-8" v-for="dorm, index in dorms" :key="index">
          <h3 class="xlarge-text mb-3 uppercase" v-html="dorm.name"></h3>
          <div class="rooms-section" :class="{ '!grid-cols-none md:!grid-cols-8': dorm.name === 'I DORM – Female' || dorm.name === 'L DORM' }">
            <div
              class="h-24 md:h-44 cursor-pointer"
              :class="[
                { 'border-black border': room.title },
                { '!pointer-events-none': room.is_full || room.title === 'Bathroom' || (room.num && !room.price) || room.disabled /*room.title === 'Laundry' || room.title === 'Bathroom' || !room.title || room.is_full */ }
              ]"
              @click="selectRoom(dorm, roomIdx)"
              v-for="room, roomIdx in dorm.rooms" :key="roomIdx"
            >
              <div
                class="room-box"
                :style="roomBgStyle(room)"
                :class="[
                  { '!bg-gray-300': room.title === 'Not available' || room.is_full || (room.num && !room.price) },
                  { '!bg-transparent': !room.title || room.title === 'Bathroom' },
                  { '!bg-accent-1': room.is_selected },
                ]"
              >
                <div class="custom-bath-box-1" v-if="dorm.name === 'E DORM — All Genders' && roomIdx === dorm.rooms.length - 1">Bathroom (A+C)</div>
                <header class="box-header">
                  <span
                    class="room-number lg:xlarge-text"
                    :class="[
                      { '!border-0': room.is_full || room.is_selected || room.title === 'Not available' || (room.num && !room.price) },
                      { '!bg-gray-400 text-white': room.is_full || room.title === 'Not available' || (room.num && !room.price) },
                      { 'bg-white text-accent-1': room.is_selected },
                    ]"
                    v-html="room.num" v-if="room.num"
                  >
                  </span>
                  <span
                    class="font-bold lg:xlarge-text text-accent-1 uppercase"
                    :class="[{ '!text-secondary-text body-bold': room.is_full }, { '!text-white': room.is_selected }]"
                    v-html="room.is_full ? '' : '$' + room.price" v-if="room.price"
                  ></span>
                </header>
                <p class="box-title" :class="[{ 'text-white': room.is_selected }, { 'hidden md:block': room.num }]">
                  <span v-html="room.is_full ? 'Not available' : room.title"></span><br>
                  <small v-html="room.subtitle" v-if="room.subtitle && !room.is_full"></small>
                </p>
              </div>
            </div>
          </div>
          <div class="room-names-btns">
            <template v-for="room, roomIdx in dorm.rooms" :key="roomIdx">
              <button
                class="room-btn"
                :class="[
                  { '!bg-gray-400 !border-gray-400 text-white': room.title === 'Not available' || room.is_full || (room.num && !room.price) },
                  { '!bg-accent-1 text-white': room.is_selected },
                  { 'col-span-full': room.title === 'Queen Plus' },
                  { '!pointer-events-none': room.is_full || room.title === 'Bathroom' || (room.num && !room.price) /*room.title === 'Laundry' || room.title === 'Bathroom' || !room.title || room.is_full */ }
                ]"
                @click="selectRoom(dorm, roomIdx)"
                v-if="room.title && room.title !== 'Bathroom'"
              >
                <strong v-html="room.num" class="text-base"></strong> {{ room.is_full ? 'Not available' : room.title }}
              </button>
            </template>
          </div>
        </div>
      </div>
      <footer class="modal-footer">
        <button
          class="btn-accent-1 btn-select body-bold"
          :disabled="!activeRoom?.title"
          @click="submitSelectedRoom"
        >
          Select room {{ activeRoom?.num }}
        </button>
      </footer>
    </div>
  </BaseModal>

  <!-- 
  <TransitionRoot appear :show="showHousingModal" as="template">
    <Dialog as="div" class="base-modal">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="backdrop" aria-hidden="true">
          <div class="container">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <div class="modal-body lg:!overflow-y-hidden">
                <DialogPanel class="add-housing-modal">
                  <button class="close-modal" @click="showHousingModal = false">
                    <Icon icon="heroicons-outline:x" class="w-6 h-6" />
                  </button>
                  <header class="modal-header">
                    <DialogTitle as="h2" class="page-heading">Add Housing</DialogTitle>
                    <h2 v-if="false" class="page-heading">Add Housing</h2>
                  </header>
                  <div class="modal-content">
                    <div class="mb-8" v-for="dorm, index in dorms" :key="index">
                      <h3 class="xlarge-text mb-3 uppercase" v-html="dorm.name"></h3>
                      <div class="rooms-section" :class="{ '!grid-cols-none md:!grid-cols-8': dorm.name === 'I DORM' || dorm.name === 'L DORM' }">
                        <div
                          class="h-24 md:h-44 cursor-pointer"
                          :class="[
                            { 'border-black border': room.title },
                            { '!pointer-events-none': room.title === 'Laundry' || room.title === 'Bathroom' || !room.title || room.is_full }
                          ]"
                          @click="selectRoom(dorm, roomIdx)"
                          v-for="room, roomIdx in dorm.rooms" :key="roomIdx">
                          <div
                            class="room-box "
                            :class="[
                              { '!bg-gray-300': room.title === 'Laundry' || room.is_full },
                              { '!bg-transparent': !room.title || room.title === 'Bathroom' },
                              { '!bg-accent-1': room.is_selected },
                            ]">
                            <div class="custom-bath-box-1" v-if="dorm.name === 'E DORM' && roomIdx === dorm.rooms.length - 1">Bathroom (A+C)</div>
                            <header class="box-header">
                              <span
                                class="room-number lg:xlarge-text"
                                :class="[
                                  { '!border-0': room.is_full || room.is_selected || room.title === 'Laundry' },
                                  { 'bg-gray-400 text-white': room.is_full || room.title === 'Laundry' },
                                  { 'bg-white text-accent-1': room.is_selected },
                                ]"
                                v-html="room.num" v-if="room.num">
                              </span>
                              <span
                                class="font-bold lg:xlarge-text text-accent-2 uppercase"
                                :class="[{ '!text-secondary-text body-bold': room.is_full }, { '!text-white': room.is_selected }]"
                                v-html="room.is_full ? 'Full' : '$' + room.price" v-if="room.price"></span>
                            </header>
                            <p class="box-title" :class="[{ 'text-white': room.is_selected }, { 'hidden md:block': room.num }]" v-html="room.title"></p>
                          </div>
                        </div>
                      </div>
                      <div class="room-names-btns">
                        <template v-for="room, roomIdx in dorm.rooms" :key="roomIdx">
                          <button
                            class="room-btn"
                            :class="[
                              { '!bg-gray-400 !border-gray-400 text-white': room.title === 'Laundry' || room.is_full },
                              { '!bg-accent-1 text-white': room.is_selected },
                              { 'col-span-full': room.title === 'Single Plus Room - Queen' }
                            ]"
                            @click="selectRoom(dorm, roomIdx)"
                            v-if="room.title && room.title !== 'Bathroom'">
                              <strong v-html="room.num" class="text-base"></strong> {{room.title}}
                          </button>
                        </template>
                      </div>
                    </div>
                    <div class="modal-actions">
                      <button class="outlined-accent-1 btn small-regular md:body-bold lg:heading-3">Farmhouse Double</button>
                      <button class="outlined-accent-1 btn small-regular md:body-bold lg:heading-3">Farmhouse Single</button>
                      <button class="outlined-accent-1 btn small-regular md:body-bold lg:heading-3" disabled>Cottage</button>
                    </div>
                  </div>
                  <footer class="modal-footer">
                    <button
                      v-if="false"
                      class="btn-accent-1 btn-select body-bold"
                      :disabled="!activeRoom.title"
                      @click="submitSelectedRoom">
                        Select room {{activeRoom.num}}
                    </button>
                    
                    <button class="btn-accent-1 btn-select body-bold" @click="showHousingModal = false">Close</button>
                  </footer>
                </DialogPanel>
              </div>
            </TransitionChild>
          </div>
      </div>
    </TransitionChild>
    </Dialog>
  </TransitionRoot>
  -->
</template>

<style lang="postcss" scoped>
.rooms-warning {
  @apply flex items-center gap-x-2.5 p-4 bg-gray-50 rounded-md
  mb-7;
  .icon {
    @apply flex-none w-5 h-5 text-secondary-text;
  }
  button {
    @apply font-bold text-accent-1 ml-1 transition hover:text-accent-2;
  }
}

.housing-meals-section {
  .housing-row {
    @apply flex flex-wrap xl:flex-nowrap items-center;
    .add-btn { @apply uppercase px-5 py-2 flex-1 min-w-[220px] xl:flex-none }
    .room-info { @apply flex items-center justify-end xl:justify-start gap-x-3 flex-wrap ml-auto xl:ml-3 mt-3 xl:mt-0 }
  }
}
/*
:deep(.add-housing-modal) {
  @apply fixed top-0 left-0 z-50 bg-white w-screen h-full lg:relative pt-20 lg:p-0 lg:w-[992px] xl:w-[1100px]
    overflow-y-auto lg:overflow-y-hidden md:text-[13px] lg:text-base;
  .close-modal {
    @apply absolute top-6 translate-y-0.5 md:translate-y-[3px] lg:translate-y-0 lg:top-8 left-5 lg:left-[initial] lg:right-5 z-20
    text-accent-1 transition hover:text-accent-2;
  }
  .modal-header .page-heading{
    @apply absolute top-0 left-0 md:-top-1 lg:static border-b lg:border-0 w-full lg:w-auto pl-12 py-6 bg-white lg:px-7 xl:px-10 lg:py-7 lg:border-b lg:mb-0
  }
  .modal-content {
    @apply lg:h-[calc(80vh-178px)] xl:h-[calc(90vh-178px)] lg:overflow-y-auto py-5 px-4 lg:px-10;
    .rooms-section {
      @apply grid grid-rows-2 grid-cols-8 grid-flow-col gap-y-[30px] mb-4 md:mb-0;
      .room-box {
        @apply flex flex-col justify-between h-full py-3 px-1.5 md:p-1.5 bg-gray-50 relative;
        .custom-bath-box-1 {
          @apply absolute bottom-full border-2 border-black w-[calc(100%+3px)] -left-0.5 lg:pl-1.5 h-8
          pointer-events-none !text-[13px] xl:!text-base hidden md:inline-flex items-center
*/
.housing-base-modal {
  :deep(.modal-body) { @apply lg:!overflow-y-hidden }
  .add-housing-modal {
    @apply fixed top-0 left-0 z-50 bg-white w-screen h-full lg:relative pt-20 lg:p-0 lg:w-[992px] xl:w-[1100px]
      overflow-y-auto lg:overflow-y-hidden md:text-[13px] lg:text-base;
    .close-modal {
      @apply absolute top-6 translate-y-0.5 md:translate-y-[3px] lg:translate-y-0 lg:top-8 left-5 lg:left-[initial] lg:right-5 z-20
      text-accent-1 transition hover:text-accent-2;
    }
    .modal-header .page-heading{
      @apply absolute top-0 left-0 md:-top-1 lg:static border-b lg:border-0 w-full lg:w-auto pl-12 py-6 bg-white lg:px-7 xl:px-10 lg:py-7 lg:border-b lg:mb-0
    }
    .modal-content {
      @apply lg:h-[calc(80vh-178px)] xl:h-[calc(90vh-178px)] lg:overflow-y-auto py-5 px-4 lg:px-10;
      .rooms-section {
        @apply grid grid-rows-2 grid-cols-8 grid-flow-col gap-y-[30px] mb-4 md:mb-0;
        .room-box {
          @apply flex flex-col justify-between h-full py-3 px-1.5 md:p-1.5 bg-gray-50 relative;
          .custom-bath-box-1 {
            @apply absolute bottom-full border-2 border-black w-[calc(100%+3px)] -left-0.5 lg:pl-1.5 h-8
            pointer-events-none !text-[13px] xl:!text-base hidden md:inline-flex items-center
          }
          .box-header {
            @apply flex flex-col md:flex-row h-full md:h-auto items-center justify-between;
            .room-number {
              @apply w-7 h-7 lg:w-10 lg:h-10 rounded-full border-2 border-accent-1 bg-white font-bold inline-flex items-center justify-center
            }
          }
          .box-title { @apply text-xs md:text-[13px] lg:text-base break-all md:break-normal leading-none }
        }
        .box-header {
          @apply flex flex-col md:flex-row h-full md:h-auto items-center justify-between;
          .room-number {
            @apply w-7 h-7 lg:w-10 lg:h-10 rounded-full border-2 border-accent-1 font-bold inline-flex items-center justify-center
          }
        }
        .box-title { @apply text-xs md:text-[13px] lg:text-base break-all md:break-normal }
      }
      .special-rooms {
        /* @apply grid grid-cols-3 gap-x-4 md:gap-x-6 items-stretch; */
        @apply flex flex-col md:flex-row gap-y-3 gap-x-5;
        .btn { @apply py-2.5 md:py-3 px-3.5 w-full md:w-auto hover:bg-transparent hover:text-primary-text rounded-none border-primary-text font-normal text-primary-text }
      }
    }
    .room-names-btns {
      @apply grid grid-cols-2 gap-x-4 gap-y-3 md:hidden;
      .room-btn {
        @apply text-left border-2 border-accent-1 py-2 px-2.5 rounded-full text-[13px]
      }
    }
    .modal-actions {
      @apply grid grid-cols-3 gap-x-4 md:gap-x-6 items-stretch;
      .btn { @apply py-2.5 md:py-5 px-3.5 }
    }
  }
  .modal-footer {
    @apply sticky bottom-0 left-0 w-full py-3 lg:py-5 bg-white text-center border-t;
    .btn-select { @apply uppercase h-11 w-60 }
  }
}
</style>